import React, { useState, useRef } from "react";
import {
  isSupportedTranscriptFile,
  MAX_UPLOAD_BYTES,
  MESSAGES,
} from "../utils/errors";

interface UploadProps {
  onFileSelect: (file: File) => void;
  onError?: (message: string) => void;
  isAnalyzing: boolean;
}

export default function Upload({
  onFileSelect,
  onError,
  isAnalyzing,
}: UploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const maxSizeMb = Math.round(MAX_UPLOAD_BYTES / 1024 / 1024);

  const reportError = (message: string) => {
    if (onError) {
      onError(message);
      return;
    }
    alert(message);
  };

  const validateAndSelect = (file: File, clearInput?: () => void) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      reportError(
        `${MESSAGES.FILE_TOO_LARGE(maxSizeMb)} (גודל הקובץ: ${(
          file.size /
          1024 /
          1024
        ).toFixed(2)}MB)`,
      );
      clearInput?.();
      return;
    }

    if (!isSupportedTranscriptFile(file.name, file.type)) {
      reportError(MESSAGES.FILE_TYPE_UNSUPPORTED);
      clearInput?.();
      return;
    }

    if (file.size === 0) {
      reportError("הקובץ שהועלה ריק. אנא העלה קובץ תקין.");
      clearInput?.();
      return;
    }

    onFileSelect(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSelect(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSelect(file, () => {
        e.target.value = "";
      });
    }
  };

  return (
    <div className="w-full">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300"}
          ${isAnalyzing ? "opacity-50 pointer-events-none" : "cursor-pointer hover:border-gray-400"}
        `}
        onClick={() => !isAnalyzing && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileInput}
          className="hidden"
          disabled={isAnalyzing}
        />

        {isAnalyzing ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">מנתח את גיליון הציונים...</p>
          </div>
        ) : (
          <>
            <svg
              className="mx-auto h-12 w-12 text-gray-400 mb-4"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-4h12m-4-4v12m0 0v-4a4 4 0 00-4-4h-4m-4-8h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-lg text-gray-700 mb-2">
              גרור ושחרר את גיליון הציונים כאן, או לחץ כדי לדפדף
            </p>
            <p className="text-sm text-gray-500">
              תומך בקבצי PDF, JPG, JPEG ו-PNG (עד {maxSizeMb}MB)
            </p>
          </>
        )}
      </div>
    </div>
  );
}
